import { countryCodeToCountry } from "lib/data/optionItems";
import { AllBuyersStats } from "lib/generated/app-api";
import { AllBuyersStatsResponse } from "lib/generated/app-api/models/AllBuyersStatsResponse";
import { SearchOrganisationsViaOsQuery } from "lib/generated/app-service-gql/graphql";
import { BuyerFromQuery } from "lib/hooks/api/buyer/useBuyerDetailsGql";
import { BuyerDetails } from "lib/types/models";
import { toTitleCase } from "lib/utils";

/**
 * Maps GraphQL buyer data to the legacy BuyerDetails format
 * This utility helps maintain compatibility with existing components
 * while using the new GraphQL API
 */
export function mapGraphQLBuyerToLegacyFormat(buyerData: BuyerFromQuery): BuyerDetails {
  return {
    id: buyerData.id,
    name: buyerData.name,
    guid: buyerData.guid,
    country: buyerData.country || "",
    url: buyerData.buyerUrl || null,
    postal_code: buyerData.postalCode || null,
    address: buyerData.address || null,
    town: buyerData.town || null,
    nuts_location: buyerData.nutsLocation || null,
    category: null,
    categories:
      buyerData.categoryAssignments?.map((cat) => ({
        buyer_category_id: cat.categoryId,
        source: cat.source || "",
      })) || [],
    metadata: {
      logo_url: buyerData.logoUrl || null,
      twitter_handle: buyerData.twitterHandle || null,
    },
    is_archived: buyerData.isArchived || false,
    is_stotles_verified: buyerData.isStotlesVerified || false,
    enhanced_data: {
      oscar_category: buyerData.oscarCategory || null,
      employee_count: buyerData.employeeCount || null,
      annual_spend: buyerData.annualSpend || null,
      location_summary: buyerData.locationSummary || null,
      url: buyerData.url || buyerData.buyerUrl || null,
    },
  };
}

/**
 * Maps Opensearch buyer search data to the legacy AllBuyersStatsResponse format
 * This utility helps maintain compatibility with existing components
 * while using Opensearch to do the queries.
 */
export function mapOsBuyerResultToLegacyFormat(
  osBuyerResult: SearchOrganisationsViaOsQuery | undefined,
): AllBuyersStatsResponse {
  if (!osBuyerResult) return { signalStats: [], totalCount: 0 };

  const legacyBuyersStats: AllBuyersStats[] = osBuyerResult?.searchOrganisations.orgs.map(
    (org) => ({
      buyerGuid: org.id,
      buyerName: org.name,
      country: (org.countryCode && countryCodeToCountry(org.countryCode)) ?? "-",
      town: toTitleCase(org.oscarTown ?? org.town ?? "-"),
      region: org.address,
      stats: {
        totalRecordCount: { count: org.noticeCount ?? 0 },
      },
      signals: [], // removed from new search display
      categories: [], // not currently visible in display
    }),
  );

  let legacyStatsResponse: AllBuyersStatsResponse = {
    signalStats: legacyBuyersStats,
    totalCount: osBuyerResult.searchOrganisations.total,
  };

  return legacyStatsResponse;
}
